import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAiUsageMetrics } from '../hooks/useAiUsageMetrics';
import { AdminKpiCard } from './AdminKpiCard';
import { downloadAiUsageCsv, type AiUsageBreakdownRow, type AiUsageMetricsFilter, type AiUsageTimeSeriesPoint } from '../api';

const TOP_PALETTE = ['#7c3aed', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#94a3b8'];

function formatCurrency(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('es-ES', { style: 'currency', currency });
  } catch {
    return `${amount.toLocaleString('es-ES')} ${currency}`;
  }
}

function formatNumber(value: number): string {
  return value.toLocaleString('es-ES');
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function shortDate(iso: string): string {
  if (iso.length < 10) return iso;
  return `${iso.substring(8, 10)}/${iso.substring(5, 7)}`;
}

/**
 * "Consumo de IA" block of the platform-admin dashboard. Reads
 * <c>GET /api/v1/admin/metrics/ai-usage</c> twice (once grouped by day for the timeline,
 * once grouped by tenant for the top-consumers table) so the dashboard stays a single
 * scrollable page without expensive client-side regroupings.
 */
export interface AiUsageSectionProps {
  /**
   * Optional tenant id. When set, every chart and KPI is scoped to that single tenant and the
   * "Top consumidores" table is hidden (it would carry a single row, which is noise).
   */
  tenantId?: string;
}

type ScopeFilter = 'All' | 'Platform' | 'Tenant';

const SCOPE_OPTIONS: ReadonlyArray<{ value: ScopeFilter; label: string }> = [
  { value: 'All', label: 'Todos' },
  { value: 'Platform', label: 'Platform' },
  { value: 'Tenant', label: 'Tenant' },
];

export function AiUsageSection({ tenantId }: AiUsageSectionProps = {}): JSX.Element {
  const to = useMemo(() => new Date(), []);
  const from = useMemo(() => {
    const d = new Date(to);
    d.setUTCDate(d.getUTCDate() - 30);
    return d;
  }, [to]);

  const tenantScoped = Boolean(tenantId);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('All');

  const dailyFilter: AiUsageMetricsFilter = useMemo(
    () => ({
      from: from.toISOString(),
      to: to.toISOString(),
      groupBy: 'Day',
      topCount: 10,
      ...(tenantId ? { tenantId } : {}),
      ...(scopeFilter !== 'All' ? { providerScope: scopeFilter } : {}),
    }),
    [from, to, tenantId, scopeFilter],
  );

  const modelFilter: AiUsageMetricsFilter = useMemo(
    () => ({
      from: from.toISOString(),
      to: to.toISOString(),
      groupBy: 'Model',
      topCount: 5,
      ...(tenantId ? { tenantId } : {}),
      ...(scopeFilter !== 'All' ? { providerScope: scopeFilter } : {}),
    }),
    [from, to, tenantId, scopeFilter],
  );

  const tenantFilter: AiUsageMetricsFilter = useMemo(
    () => ({
      from: from.toISOString(),
      to: to.toISOString(),
      groupBy: 'Tenant',
      topCount: 10,
      ...(scopeFilter !== 'All' ? { providerScope: scopeFilter } : {}),
    }),
    [from, to, scopeFilter],
  );

  const daily = useAiUsageMetrics(dailyFilter);
  const byModel = useAiUsageMetrics(modelFilter);
  // Top-consumers only makes sense at the platform level; skip the extra query when scoped.
  const byTenant = useAiUsageMetrics(tenantFilter);
  const includeByTenant = !tenantScoped;

  const isLoading = daily.isLoading || byModel.isLoading || (includeByTenant && byTenant.isLoading);
  const isError = daily.isError || byModel.isError || (includeByTenant && byTenant.isError);
  const summary = daily.data?.summary ?? null;
  const currency = summary?.currency ?? 'USD';

  const tokenSeries = (daily.data?.series ?? []).map((p: AiUsageTimeSeriesPoint) => ({
    day: shortDate(p.bucketKey),
    input: p.inputTokens,
    output: p.outputTokens,
  }));

  const costSeries = (daily.data?.series ?? []).map((p: AiUsageTimeSeriesPoint) => ({
    day: shortDate(p.bucketKey),
    cost: Number(p.cost.toFixed(4)),
  }));

  const modelTop5 = (byModel.data?.top ?? []).slice(0, 5);
  const otherCost = (byModel.data?.summary.costTotal ?? 0) - modelTop5.reduce((acc, m) => acc + m.cost, 0);
  const modelPie = [
    ...modelTop5.map((m, i) => ({ name: m.label, value: Number(m.cost.toFixed(4)), color: TOP_PALETTE[i % TOP_PALETTE.length] })),
    ...(otherCost > 0
      ? [{ name: 'Otros', value: Number(otherCost.toFixed(4)), color: TOP_PALETTE[5] }]
      : []),
  ];

  const topTenants = (byTenant.data?.top ?? []).slice(0, 10);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadAiUsageCsv(dailyFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `ai-usage-${stamp}${tenantId ? `-${tenantId}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Descarga fallida.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">
          Consumo de IA — últimos 30 días{tenantScoped ? ' (este tenant)' : ''}
        </h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5 text-xs">
            {SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setScopeFilter(option.value)}
                className={`rounded px-2 py-1 font-medium transition-colors ${
                  scopeFilter === option.value
                    ? 'bg-violet-600 text-white'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                title={
                  option.value === 'Platform'
                    ? 'Solo proveedores del panel de administración'
                    : option.value === 'Tenant'
                      ? 'Solo proveedores configurados por el tenant'
                      : 'Sin filtro de scope'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            onClick={() => void handleDownload()}
            disabled={downloading}
            title="Descarga las filas crudas del periodo aplicando los mismos filtros."
          >
            {downloading ? 'Descargando…' : 'Descargar CSV'}
          </button>
        </div>
      </header>
      {downloadError ? (
        <div className="card border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {downloadError}
        </div>
      ) : null}

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          No se pudieron cargar las métricas de consumo de IA.
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminKpiCard
          label="Coste 30d"
          value={isLoading || !summary ? '…' : formatCurrency(summary.costTotal, currency)}
          sub="suma de filas con precio resuelto"
        />
        <AdminKpiCard
          label="Llamadas 30d"
          value={isLoading || !summary ? '…' : formatNumber(summary.callCount)}
          {...(summary
            ? { sub: `${formatNumber(summary.inputTokensTotal + summary.outputTokensTotal)} tokens` }
            : {})}
        />
        <AdminKpiCard
          label="Tasa de éxito"
          value={isLoading || !summary ? '…' : `${(summary.successRate * 100).toFixed(1)}%`}
        />
        <AdminKpiCard
          label="Latencia media"
          value={isLoading || !summary ? '…' : formatMs(summary.averageLatencyMs)}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <p className="mb-3 text-sm font-medium text-slate-700">Tokens por día (input + output)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={tokenSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="input" stroke="#7c3aed" name="Input" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="output" stroke="#0ea5e9" name="Output" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5">
          <p className="mb-3 text-sm font-medium text-slate-700">Coste por día ({currency})</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v, currency)} />
                <Bar dataKey="cost" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className={tenantScoped ? 'grid gap-4' : 'grid gap-4 lg:grid-cols-2'}>
        <div className="card p-5">
          <p className="mb-3 text-sm font-medium text-slate-700">% de coste por modelo</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={modelPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={84} paddingAngle={2}>
                  {modelPie.map((slice) => (
                    <Cell key={slice.name} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatCurrency(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {tenantScoped ? null : (
        <div className="card overflow-x-auto p-0">
          <div className="p-5 pb-3">
            <p className="text-sm font-medium text-slate-700">Top consumidores (por coste)</p>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2 text-right">Llamadas</th>
                <th className="px-4 py-2 text-right">Tokens</th>
                <th className="px-4 py-2 text-right">Coste</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {topTenants.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-slate-500">Sin datos en la ventana.</td></tr>
              ) : (
                topTenants.map((row: AiUsageBreakdownRow) => (
                  <tr key={row.key}>
                    <td className="px-4 py-2 font-medium text-slate-900">
                      {row.key === '(platform)' ? (
                        <span className="text-slate-500">{row.label}</span>
                      ) : (
                        <Link className="text-violet-700 hover:underline" to={`/admin/tenants/${encodeURIComponent(row.key)}`}>
                          {row.label}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatNumber(row.callCount)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatNumber(row.inputTokens + row.outputTokens)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(row.cost, currency)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </section>
  );
}
